// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CalendarPlus, Loader2, RefreshCw } from 'lucide-react';
import { getDivisionColor } from '@/utils/divisionColors';
import { PublicEventHeader } from '@/components/public/PublicEventHeader';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { usePublicLive, useDivisionParam, formatUpdated } from '@/hooks/usePublicLive';
import { PublicGameSections } from '@/components/public/PublicGameRow';

const FN = 'rle-public-schedule';
const NAVY = '#0a1f44';

function downloadICS(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PublicEventTeamPage: React.FC = () => {
  const { slug, teamId } = useParams<{ slug: string; teamId: string }>();
  const [division] = useDivisionParam();
  const [downloading, setDownloading] = useState(false);

  const { data, loading, errorCode, lastUpdated, refreshing, refresh } = usePublicLive(
    FN,
    { action: 'get', slug },
    { enabled: !!slug },
  );

  const games = useMemo(() => {
    const flat: any[] = [];
    (data?.days || []).forEach((day: any) =>
      (day.games || []).forEach((g: any) => flat.push(g)),
    );
    return flat
      .filter((g) => g.team_a_id === teamId || g.team_b_id === teamId)
      .sort((a, b) =>
        `${a.date || ''} ${a.start || ''}`.localeCompare(`${b.date || ''} ${b.start || ''}`),
      );
  }, [data, teamId]);

  const teamName = useMemo(() => {
    const t = (data?.teams || []).find((x: any) => x.id === teamId);
    if (t) return t.name;
    const g = games[0];
    if (!g) return 'Team';
    return g.team_a_id === teamId ? g.team_a : g.team_b;
  }, [data, teamId, games]);

  const record = useMemo(() => {
    let w = 0;
    let l = 0;
    let t = 0;
    for (const g of games) {
      if (g.status !== 'completed') continue;
      const isA = g.team_a_id === teamId;
      const mine = isA ? g.score_a ?? 0 : g.score_b ?? 0;
      const theirs = isA ? g.score_b ?? 0 : g.score_a ?? 0;
      if (mine > theirs) w += 1;
      else if (mine < theirs) l += 1;
      else t += 1;
    }
    return { w, l, t, played: w + l + t };
  }, [games, teamId]);

  const handleICS = async () => {
    setDownloading(true);
    try {
      const { data: res, error } = await invokeEdgeFunction(FN, {
        body: { action: 'ics', slug, team_id: teamId },
      });
      if (error || !res?.ok) return;
      const payload = res.data || res;
      if (payload?.content && payload?.filename) downloadICS(payload.filename, payload.content);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorCode === 'NOT_PUBLISHED') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          This schedule hasn't been published yet.
        </h1>
      </div>
    );
  }

  if (errorCode || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Could not load this team.</h1>
      </div>
    );
  }

  const event = data.event || {};
  const teamDivision = games[0]?.division;
  const accent = getDivisionColor(teamDivision);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <PublicEventHeader
        slug={slug ?? ''} current="schedule" division={division}
        event={event} branding={data.branding} tabs={data.tabs || []} teams={data.teams || []}
      />

      <section className="mb-6">
        <h2 className="text-3xl font-bold" style={{ color: NAVY }}>{teamName}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {teamDivision && (
                <span
                  className="mr-2 rounded-full px-2.5 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: accent.bg, color: accent.text }}
                >
                  {teamDivision}
                </span>
              )}
              {record.played > 0
                ? `${record.w}-${record.l}${record.t ? `-${record.t}` : ''} · ${games.length} game${games.length === 1 ? '' : 's'}`
                : `${games.length} game${games.length === 1 ? '' : 's'} · no results yet`}
            </p>
      </section>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={handleICS} disabled={downloading}>
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CalendarPlus className="h-4 w-4" />
          )}
          Add to calendar
        </Button>
        <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <span className="text-xs text-muted-foreground">
          Updated {formatUpdated(lastUpdated)}
        </span>
      </div>

      <PublicGameSections games={games} slug={slug ?? ''} selectedTeamId={teamId} />
    </div>
  );
};

export default PublicEventTeamPage;
